'use client';

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { ChevronLeft, ChevronRight, FileText, Search, Target, CheckSquare, Footprints, Link2, MapPin, Dumbbell } from 'lucide-react';

interface ProductItem {
  image?: string;
  icon?: string;
  title: string;
  description: string;
}

interface ProductCarouselProps {
  items: ProductItem[];
  autoPlay?: boolean;
  interval?: number;
}

const iconMap: Record<string, React.ReactNode> = {
  'file': <FileText className="w-16 h-16 text-white" />,
  'search': <Search className="w-16 h-16 text-white" />,
  'target': <Target className="w-16 h-16 text-white" />,
  'check': <CheckSquare className="w-16 h-16 text-white" />,
  'running': <Footprints className="w-16 h-16 text-white" />,
  'link': <Link2 className="w-16 h-16 text-white" />,
  'marker': <MapPin className="w-16 h-16 text-white" />,
  'dumbbell': <Dumbbell className="w-16 h-16 text-white" />,
};

export default function ProductCarousel({ items, autoPlay = true, interval = 5000 }: ProductCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(autoPlay);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const autoPlayRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying) return;

    autoPlayRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % items.length);
    }, interval);

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [isAutoPlaying, items.length, interval]);

  // Scroll to current item
  useEffect(() => {
    if (!scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const itemWidth = container.scrollWidth / items.length;
    const scrollPosition = currentIndex * itemWidth;

    container.scrollTo({
      left: scrollPosition,
      behavior: 'smooth'
    });
  }, [currentIndex, items.length]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? items.length - 1 : prev - 1));
    setIsAutoPlaying(false);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % items.length);
    setIsAutoPlaying(false);
  };

  return (
    <div className="relative w-full">
      {/* Carousel Container */}
      <div
        ref={scrollContainerRef}
        className="flex overflow-x-auto snap-x snap-mandatory gap-6 pb-4"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {items.map((item, index) => (
          <div
            key={index}
            className="flex-none w-full sm:w-1/2 lg:w-1/3 snap-start"
          >
            <div className="bg-white rounded-[30px] p-6 shadow-lg hover:shadow-xl transition-shadow h-full flex flex-col border border-gray-100">
              {/* Image or Icon */}
              <div
                className="w-full h-56 flex items-center justify-center mb-5 rounded-2xl overflow-hidden"
                style={{
                  background: item.image
                    ? 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)'
                    : 'linear-gradient(135deg, #1565C0 0%, #1976D2 50%, #1E88E5 100%)'
                }}
              >
                {item.image ? (
                  <div className="relative w-full h-full">
                    <Image
                      src={item.image}
                      alt={item.title}
                      fill
                      className="object-contain p-4"
                    />
                  </div>
                ) : (
                  item.icon && iconMap[item.icon] ? iconMap[item.icon] : (
                    <div className="text-7xl text-white">📦</div>
                  )
                )}
              </div>

              {/* Title */}
              <h3 className="text-xl font-bold mb-3 text-cta">
                {item.title}
              </h3>

              {/* Description */}
              <p className="text-ink/70 leading-relaxed flex-grow text-sm">
                {item.description}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={goToPrevious}
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 md:-translate-x-4 bg-white border-2 border-cta rounded-full p-3 shadow-lg hover:bg-cta hover:text-white transition-colors z-10"
        aria-label="Vorig item"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>

      <button
        onClick={goToNext}
        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 md:translate-x-4 bg-white border-2 border-cta rounded-full p-3 shadow-lg hover:bg-cta hover:text-white transition-colors z-10"
        aria-label="Volgend item"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      {/* Dots Indicator */}
      <div className="flex justify-center gap-2 mt-6">
        {items.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className="p-3 -m-2"
            aria-label={`Ga naar item ${index + 1}`}
          >
            <span className={`block h-3 rounded-full transition-all ${
              index === currentIndex
                ? 'bg-cta w-8'
                : 'bg-ink/20 w-3'
            }`} />
          </button>
        ))}
      </div>
    </div>
  );
}
